import { useState } from "react";
import type { FormulaBlock } from "../contract/types";
import { Icon } from "../components/Icon";
import { Tex, TexText } from "../components/Math";
import type { BlockViewProps } from "./types";

/**
 * Fórmula en display, con la leyenda de variables y —si el agente la envía—
 * la derivación paso a paso.
 *
 * La leyenda de variables no es decorativa: en este curso media nota se pierde
 * por confundir Ke con Kd o E con V, así que cada símbolo viene con su
 * significado y su unidad.
 */
export function Formula({ block }: BlockViewProps<FormulaBlock>) {
  const [showDerivation, setShowDerivation] = useState(false);
  const variables = block.variables ?? [];
  const derivation = block.derivation ?? [];

  return (
    <figure className="atom atom--formula formula">
      <div className="formula__display">
        <Tex latex={block.latex} display />
      </div>

      {block.caption ? <figcaption className="formula__caption">{block.caption}</figcaption> : null}

      {variables.length > 0 ? (
        <dl className="formula__variables">
          {variables.map((variable) => (
            <div key={variable.symbol} className="formula__variable">
              <dt className="formula__symbol">
                <Tex latex={variable.symbol} />
              </dt>
              <dd className="formula__meaning">
                <span className="formula__meaning-text">{variable.meaning}</span>
                {variable.unit ? <span className="formula__unit">{variable.unit}</span> : null}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {derivation.length > 0 ? (
        <div className="formula__derivation">
          <button
            type="button"
            className="formula__toggle"
            aria-expanded={showDerivation}
            onClick={() => setShowDerivation((value) => !value)}
          >
            <Icon name={showDerivation ? "chevron-down" : "chevron-right"} size="0.875rem" />
            <span>{showDerivation ? "Ocultar derivación" : `Ver derivación (${derivation.length} pasos)`}</span>
          </button>
          {showDerivation ? (
            <ol className="formula__steps">
              {derivation.map((step, index) => (
                <li key={index} className="formula__step">
                  <span className="formula__step-index" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span className="formula__step-body">
                    <TexText text={step} />
                  </span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}
    </figure>
  );
}

export default Formula;
